import {
    NinchatActions,
    NinchatEvents,
    NinchatProperties,
} from '../../api/types'
import {
    NinchatCallActionMeta,
    clientActionTypes,
} from '../../actions/ninchat/client/types'
import {
    clearFileUpload,
    fileQueue,
    updateFileUpload,
} from '../../state/ui'
import {AttachmentFile} from '../../types/ui'
import {Middleware} from 'redux'
import {NinchatActionType} from '../../api/types/actions'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import NinchatClient from 'ninchat-js'
import {getEnvironment} from '../../appconfig'

export const sessionlessMiddleware: Middleware = ({dispatch}) => {
    const {
        apiHost,
        apiVersion,
        siteSecret,
        xUserAgent,
    } = getEnvironment()

    const log = () => null

    const readFile = (file: File) => new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
            resolve(reader.result)
        }

        reader.onerror = () => {
            reject(reader.error)
        }

        reader.readAsArrayBuffer(file)
    })

    const buildBody = (action: NinchatActions.NinchatAction, data: ArrayBuffer) => {
        const actionBytes = new TextEncoder().encode(JSON.stringify(action))
        const body = new ArrayBuffer(1 + 2 + actionBytes.byteLength + 1 + 8 + data.byteLength)
        const view = new DataView(body)

        let offset = 0
        view.setUint8(offset, 126) // 2-byte length encoding
        offset += 1
        view.setUint16(offset, actionBytes.byteLength) // backend limit is 32 kB
        offset += 2
        new Uint8Array(body, offset).set(actionBytes)
        offset += actionBytes.byteLength
        view.setUint8(offset, 127) // 8-byte length encoding
        offset += 1
        view.setUint32(offset + 0, 0) // backend limit is 32 MB
        view.setUint32(offset + 4, data.byteLength)
        offset += 8
        new Uint8Array(body, offset).set(new Uint8Array(data))
        offset += data.byteLength

        return body
    }

    const call = ({
        header, payload, meta,
    }: {header: NinchatActions.NinchatAction, payload: NinchatProperties.PayloadDataFrame[], meta?: NinchatCallActionMeta}): Promise<NinchatEvents.NinchatEvent> => {
        const {caller} = meta ?? {}

        const params = {
            ...header,
            ...caller && {...caller},
            ...siteSecret && {site_secret: siteSecret},
            ...payload && {...payload},
        }
        return NinchatClient.call(params, log, apiHost)
    }

    const callxhr = async ({
        header, payload, meta,
    }: {header: NinchatActions.NinchatSendFileAction | NinchatActions.NinchatSendFileUnlinkedAction, payload: AttachmentFile, meta: Required<NinchatCallActionMeta>}) => {
        const {
            caller,
            xhr: {
                method,
                type,
            },
        } = meta

        const action = {
            ...header,
            ...caller,
            ...siteSecret && {site_secret: siteSecret},
        }

        const file = fileQueue.get(payload.name)


        if (!file) {
            return Promise.reject(new Error('file: data missing'))
        }

        const chat_id = ('channel_id' in header ? header.channel_id : undefined) ?? header.user_id
        const data = await readFile(file) as ArrayBuffer
        const body = buildBody(action, data)

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()

            xhr.onload = () => {
                try {
                    const event = JSON.parse(xhr.responseText)

                    if (event.event === 'error') {
                        reject(event)
                    } else if (chat_id) {
                        if (header.action === NinchatActionType.NinchatSendFileUnlinkedAction) {
                            dispatch(updateFileUpload({
                                chat_id,
                                // eslint-disable-next-line no-extra-parens
                                data: {file_id: (event as NinchatEvents.NinchatFileUploadedEvent).file_id},
                                filename: action.file_attrs.name,
                            }))
                        } else {
                            dispatch(clearFileUpload({
                                chat_id,
                                filename: action.file_attrs.name,
                            }))
                        }
                        resolve([event])
                    } else {
                        resolve([event])
                    }
                } catch (error) {
                    reject(new Error('xhr: api error'))
                }
            }

            xhr.onerror = () => {
                reject(new Error('xhr: network error'))
            }

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable && chat_id) {
                    const progress = Math.floor(event.loaded / event.total * 100)
                    dispatch(updateFileUpload({
                        chat_id,
                        data: {progress},
                        filename: action.file_attrs.name,
                    }))
                }
            }

            xhr.open(method, `https://${apiHost}/${apiVersion}/call`, true)
            xhr.setRequestHeader('Accept', 'application/json')
            xhr.setRequestHeader('Content-Type', type)
            xhr.setRequestHeader('X-User-Agent', xUserAgent)

            xhr.send(body)
        })
    }

    return (next) => (action) => {
        switch (action.type) {
            case clientActionTypes.CALL: {
                let promise = null
                if (action.payload?.meta?.xhr &&
                    (action.payload.header.action === NinchatActionType.NinchatSendFileAction || action.payload.header.action === NinchatActionType.NinchatSendFileUnlinkedAction)) {
                    promise = callxhr(action.payload)
                } else {
                    promise = call(action.payload)
                }
                next(action)

                return promise
            }
            default:
                return next(action)
        }
    }
}
